import { useCallback, useEffect, useRef, useState } from 'react';
import { ShieldCheck, X } from 'lucide-react';
import Button from '../common/Button.js';

const LoginOtpModal = ({
  open,
  challenge,
  verifying = false,
  resending = false,
  error = '',
  onClose,
  onVerify,
  onResend,
}) => {
  const [otp, setOtp] = useState('');
  const [resendWait, setResendWait] = useState(0);
  const submittedRef = useRef('');
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setOtp('');
      submittedRef.current = '';
      setResendWait(0);
      return;
    }
    setOtp('');
    submittedRef.current = '';
    setResendWait(Number(challenge?.resend_available_in_seconds) || 0);
    const timer = window.setTimeout(() => inputRef.current?.focus(), 50);
    return () => window.clearTimeout(timer);
  }, [open, challenge?.challenge_id]);

  useEffect(() => {
    if (!open) return undefined;
    const nextWait = Number(challenge?.resend_available_in_seconds);
    if (Number.isFinite(nextWait) && nextWait > 0) {
      setResendWait(nextWait);
    }
    return undefined;
  }, [open, challenge?.resend_available_in_seconds, challenge?.challenge_id]);

  useEffect(() => {
    if (!open) return undefined;
    const timer = window.setInterval(() => {
      setResendWait((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [open]);

  useEffect(() => {
    if (error) {
      submittedRef.current = '';
    }
  }, [error]);

  useEffect(() => {
    if (resending) {
      setOtp('');
      submittedRef.current = '';
    }
  }, [resending]);

  useEffect(() => {
    if (!open || !('OTPCredential' in window) || !navigator.credentials?.get) {
      return undefined;
    }
    const ac = new AbortController();
    navigator.credentials
      .get({ otp: { transport: ['sms'] }, signal: ac.signal })
      .then((cred) => {
        const code = String(cred?.code || '').replace(/\D/g, '').slice(0, 6);
        if (code) setOtp(code);
      })
      .catch(() => {});
    return () => ac.abort();
  }, [open, challenge?.challenge_id]);

  const submitOtp = useCallback(
    (value) => {
      const code = String(value || '').replace(/\D/g, '').slice(0, 6);
      if (code.length !== 6 || verifying) return;
      if (submittedRef.current === code) return;
      submittedRef.current = code;
      onVerify?.(code);
    },
    [onVerify, verifying],
  );

  const handleChange = (value) => {
    const next = String(value || '').replace(/\D/g, '').slice(0, 6);
    if (next !== submittedRef.current) {
      submittedRef.current = '';
    }
    setOtp(next);
    if (next.length === 6) {
      submitOtp(next);
    }
  };

  if (!open) return null;

  const masked = challenge?.masked_phone || 'WhatsApp';
  const expiresIn = Number(challenge?.expires_in_seconds) || 0;
  const expiresLabel = expiresIn
    ? ` · valid ${Math.max(1, Math.ceil(expiresIn / 60))} min`
    : '';

  return (
    <div className="fixed inset-0 z-[120] flex items-end sm:items-center justify-center bg-black/50 p-0 sm:p-4">
      <div className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl shadow-xl">
        <div className="flex items-start justify-between gap-3 p-4 border-b border-gray-100">
          <div>
            <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-[#3467ae]" />
              Verify login OTP
            </h3>
            <p className="text-xs text-gray-500 mt-1">
              OTP was sent on WhatsApp to {masked}
              {expiresLabel}. Enter the 6-digit code to continue.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded-lg text-gray-500 hover:bg-gray-100"
            aria-label="Close"
            disabled={verifying}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-3">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          <input
            ref={inputRef}
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            name="otp"
            value={otp}
            onChange={(e) => handleChange(e.target.value)}
            placeholder="6-digit OTP"
            maxLength={6}
            disabled={verifying}
            className="w-full px-3 py-3 text-lg tracking-[0.4em] text-center border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />

          <Button
            type="button"
            variant="primary"
            fullWidth
            onClick={() => submitOtp(otp)}
            loading={verifying}
            disabled={verifying || otp.length !== 6}
          >
            {verifying ? 'VERIFYING...' : 'VERIFY & SIGN IN'}
          </Button>

          <button
            type="button"
            onClick={onResend}
            disabled={resending || verifying || resendWait > 0}
            className="w-full text-xs text-[#3467ae] hover:underline disabled:opacity-50 disabled:no-underline"
          >
            {resending
              ? 'Resending...'
              : resendWait > 0
                ? `Resend OTP in ${resendWait}s`
                : 'Resend OTP'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default LoginOtpModal;
